/**
 * UI State - Reactive state shared between UI and game
 *
 * UI reads this state reactively via Valtio, each component for the top-level
 * keys it reads (ValtioController); game code updates it to reflect changes.
 * Plain data only. A value written on every step of a drag gets a top-level
 * key of its own, and a feed's entries are pushed as Valtio refs; CLAUDE.md
 * has both rules. `uiStateReaders(uiState)` in the dev console lists which
 * component reads each key, the check to make before calling a key dead: the
 * chat's name completion reads `players` only while a name is being typed.
 */

import { proxy } from 'valtio/vanilla';
import { DISPLAY_DEFAULTS, type DisplaySettingValues, GAME_DEFAULTS, type GameSettingValues } from '../game/Config';
import type { IdentityProviderId } from '../game/types/Auth';
import { ALT_SCALE_DEFAULT, MINIMAP_SCALE_DEFAULT, SCALE_DEFAULT, type PlaneType } from '../game/Constants';
import type { GameRegion } from '../game/types/Games';
import type { BindName, BindSlot } from '../game/types/Input';
import type { TeamId } from '../game/types/Players';
import type { FlagCode } from '../generated/flags';
import { UNKNOWN_FLAG } from '../services/Flags';

/** Panel types that can be shown in the overlay layer */
export type PanelType = 'login' | 'help' | 'score' | 'invite' | 'keybinds' | 'mainmenu' | 'radioconfig' | 'emotes';

/** The menu drawer's pages: the menu itself, and the Country Flag picker */
export type MenuPage = 'main' | 'flag';

/** Top-bar popup panels — mutually exclusive (only one open at a time) */
export type TopBarPanel = 'game-selector' | 'zoom-slider' | 'loadout-panel';

/** Single keybind item for UI display */
export interface KeybindItem {
  /** Display name shown to user (e.g., "Forward", "Fire") */
  displayName: string;
  /** Internal bind name (e.g., "UP", "FIRE") */
  bindName: BindName;
  /** Primary key display text (e.g., "UP", "W"), null if unbound */
  primaryKey: string | null;
  /** Secondary key display text, null if unbound */
  secondaryKey: string | null;
}

/** Keybinds panel state */
export interface KeybindsState {
  /** Left column keybinds (the first half, plus the odd one out) */
  leftColumn: KeybindItem[];
  /** Right column keybinds (the rest) */
  rightColumn: KeybindItem[];
  /** Currently editing bind name (null if not editing) */
  editingBindName: BindName | null;
  /** The slot being edited (0 = primary, 1 = secondary); kept when the edit ends */
  editingBindIndex: BindSlot;
  /** Map of bind name → primary-key display label (e.g. `SOUND` → "G").
   *  Mirrors the live keybind config so shortcut hints in the UI (toolbar
   *  tooltips, menu rows) follow user remaps. Only bound actions appear;
   *  an unbound action is absent. */
  bindByName: Partial<Record<BindName, string>>;
}

/**
 * Display settings state (mirrors settings.display). The menu's settings are
 * the DisplaySettingValues, mirrored by GameAPI's display group; the sizes
 * below are mirrored by their own setters.
 */
export interface DisplaySettingsState extends DisplaySettingValues {
  /** Saved chat panel width in pixels (null = default CSS) */
  chatWidth: number | null;
  /** Saved chat panel height in pixels (null = default CSS) */
  chatHeight: number | null;
  /** Minimap scale multiplier (0.75 to 3.0) */
  minimapScale: number;
}

/**
 * Zoom state (mirrors the top-level zoom settings: settings.scalingFactor,
 * settings.altScalingFactor and settings.scalingAltMode, which predate
 * settings.display). A key of its own rather than part of displaySettings: a
 * zoom drag writes the live factor on every step, and a top-level key wakes
 * only the components that read it, so this keeps those steps to the zoom
 * controls and off the other display settings' readers.
 */
export interface ZoomState {
  /** Main zoom level (800-7000) */
  scalingFactor: number;
  /** Alternate zoom level (800-7000) */
  altScalingFactor: number;
  /** Whether alternate zoom mode is active */
  scalingAltMode: boolean;
  /** The widest zoom the server allows on this viewport, rounded; null before login. The saved
   *  zoom stays as set, and the game applies it no wider than this (UI.getScalingFactor). */
  serverCap: number | null;
}

/**
 * Game settings state (mirrors settings.game): the GameSettingValues,
 * mirrored by GameAPI's game group, and the one setting saved elsewhere.
 */
export interface GameSettingsState extends GameSettingValues {
  /** Mouse mode: left click fires, right click uses the special. Not under
   *  settings.game: it's the legacy top-level `mousemode` setting, mirrored
   *  here by Input's setMouseMode (the M key and the menu's switch). */
  mouseMode: boolean;
}

/** Player info for game log entries */
export interface GameLogPlayer {
  id: number;
  name: string;
  flag: FlagCode;
  team: TeamId;
  isMe: boolean;
  isBot: boolean;
}

/** Kill event in game log */
export interface GameLogKillEntry {
  id: number;
  type: 'kill';
  killer: GameLogPlayer;
  victim: GameLogPlayer;
}

/** Player join event in game log */
export interface GameLogJoinEntry {
  id: number;
  type: 'join';
  player: GameLogPlayer;
}

/** Player leave event in game log */
export interface GameLogLeaveEntry {
  id: number;
  type: 'leave';
  player: GameLogPlayer;
}

/** CTF flag action type */
export type CtfFlagAction = 'captured' | 'returned' | 'taken';

/** CTF flag team (which team's flag) */
export type CtfFlagTeam = 'blue' | 'red';

/**
 * CTF flag event in game log
 *
 * BRITTLE: These events are parsed from SERVER_MESSAGE HTML text sent by the server.
 * The format is: `<span class="info inline"><span class="(blueflag|redflag)"></span></span>(Captured|Returned|Taken) by [PlayerName]`
 * If the server changes this format, parsing will break silently.
 *
 * @see UI.ts parseCtfFlagEvent() for the parsing logic
 */
export interface GameLogCtfFlagEntry {
  id: number;
  type: 'ctf-flag';
  /** The action performed (captured = scored, returned = flag back to base, taken = picked up) */
  action: CtfFlagAction;
  /** Which team's flag (blue or red) */
  flagTeam: CtfFlagTeam;
  /** Team of the player who performed the action — derived from CTF rules at
   *  construction (captured = opposite of flagTeam, returned = same as
   *  flagTeam). Stored so the render layer doesn't have to know the rules. */
  playerTeam: CtfFlagTeam;
  /** Player name who performed the action (not full player info - we only get the name from server) */
  playerName: string;
}

/**
 * BTR win event in game log
 *
 * Displayed when a player wins a Battle Royale round.
 * Data comes from SERVER_CUSTOM type 1 message.
 */
export interface GameLogBtrWinEntry {
  id: number;
  type: 'btr-win';
  /** Winner's player name */
  playerName: string;
  /** Winner's country flag code */
  flag: FlagCode;
  /** Number of kills achieved */
  kills: number;
  /** Bounty reward */
  bounty: number;
}

/**
 * BTR players alive event in game log
 *
 * Displayed when the number of players alive changes in Battle Royale.
 * Data comes from GAME_PLAYERSALIVE message.
 */
export interface GameLogPlayersAliveEntry {
  id: number;
  type: 'players-alive';
  /** Number of players currently alive */
  playersAlive: number;
}

/** Game log entry for kill feed display */
export type GameLogEntry =
  | GameLogKillEntry
  | GameLogJoinEntry
  | GameLogLeaveEntry
  | GameLogCtfFlagEntry
  | GameLogBtrWinEntry
  | GameLogPlayersAliveEntry;

/**
 * CTF match result notice state
 *
 * Displayed when a CTF match ends, showing victory/defeat and bounty reward.
 * Auto-dismisses after the duration specified by the server.
 * null = hidden, non-null = visible
 */
export interface CtfMatchResultState {
  /** Whether our team won (true = victory, false = defeat). Spectators keep a
   *  team, so they get their team's result, as in StarMash. null only if our
   *  own player isn't in the roster, which doesn't happen in play. */
  isVictory: boolean | null;
  /** Which team won the match (1 = blue, 2 = red) — shown as the BLUE TEAM /
   *  RED TEAM line under the bounty, StarMash-style */
  winnerTeam: 1 | 2;
  /** Bounty reward amount */
  bounty: number;
  /** Auto-dismiss duration in seconds */
  duration: number;
}

/**
 * BTR match result notice state
 *
 * Displayed when a BTR round ends, showing the winner's name, flag, kills, and bounty.
 * Auto-dismisses after the duration specified by the server.
 * null = hidden, non-null = visible
 */
export interface BtrMatchResultState {
  /** Winner's player name */
  playerName: string;
  /** Winner's country flag ISO code (e.g., 'us', 'gb') */
  flag: FlagCode;
  /** Number of kills achieved by the winner */
  kills: number;
  /** Bounty reward amount */
  bounty: number;
  /** Auto-dismiss duration in seconds */
  duration: number;
}

/**
 * Level up notification state
 *
 * Displayed when the player reaches a new level. The component owns its
 * auto-dismiss timer, and holds the notice back while a match result shows.
 * null = none
 */
export interface LevelUpState {
  /** The new level number */
  level: number;
}

/** Per-flag status for CTF in-game HUD */
export interface CtfFlagStatus {
  /** Name of player carrying the flag (null = not carried) */
  carrierName: string | null;
  /** Number of successful flag captures for this team (0-3) */
  captures: number;
}

/** CTF in-game HUD state showing flag carriers and capture scores */
export interface CtfHudState {
  blue: CtfFlagStatus;
  red: CtfFlagStatus;
}

/** A non-bot, non-spec player on a specific plane type (used for plane-count tooltip). */
export interface CtfPlaneRosterEntry {
  name: string;
}

/** Per-plane-type roster: which non-bot players are flying this plane, split by team. */
export interface CtfPlaneRoster {
  blue: CtfPlaneRosterEntry[];
  red: CtfPlaneRosterEntry[];
}

/** Minimap screen rectangle for DOM positioning calculations */
export interface MinimapRect {
  /** Top edge in screen pixels (from top of viewport) */
  top: number;
  /** Left edge in screen pixels (from left of viewport) */
  left: number;
  /** Width in screen pixels */
  width: number;
  /** Height in screen pixels (half of width due to 2:1 aspect) */
  height: number;
}

/** Invite link data parsed from URL hash */
export interface InviteData {
  /** Region ID from invite (e.g., "eu") */
  regionId: string;
  /** Room/game ID from invite (e.g., "ffa1") */
  roomId: string;
  /** Optional inviter name */
  inviterName?: string;
  /** Whether the invite matches a valid region/game */
  isValid: boolean;
  /** Resolved region name for display (e.g., "Europe") */
  regionName?: string;
  /** Resolved game name for display (e.g., "FFA #1") */
  gameName?: string;
}

/** Identity provider types for OAuth */
export type IdentityProvider = 'microsoft' | 'google' | 'twitter' | 'reddit' | 'twitch';

// =============================================================================
// Scoreboard Types
// =============================================================================

/** The game modes, as the session's game type and as a scoreboard's layout */
export type GameMode = 'ffa' | 'ctf' | 'btr';

/** Individual player score entry (plain data for UI) */
export interface ScoreboardPlayer {
  /** Player ID for context menu interactions */
  id: number;
  /** Display rank (1-based, preserves original position even when filtering) */
  rank: number;
  /** Player display name */
  name: string;
  /** Country flag ISO code (e.g., 'us', 'gb') */
  flag: FlagCode;
  /** Country name for the flag's hover title (e.g., "United Kingdom (gb)") */
  flagLabel: string;
  /** Whether player is a bot */
  isBot: boolean;
  /** Team id; TeamId says what it means per game type */
  team: TeamId;
  /** Whether player is spectating */
  isSpectating: boolean;
  /** Whether this is the current player */
  isMe: boolean;
  /** Kill count */
  kills: number;
  /** Death count */
  deaths: number;
  /** Bounty score */
  score: number;
  /** Player level */
  level: number;
  /** Ping in milliseconds */
  ping: number;
  /** Damage dealt (FFA mode only) */
  damage?: number;
  /** Flag captures (CTF mode only) */
  captures?: number;
  /** Round wins (BTR mode only) */
  wins?: number;
}

/** MVP display data */
export interface ScoreboardMvp {
  /** Player name */
  name: string;
  /** Country flag ISO code (e.g., 'us', 'gb') */
  flag: FlagCode;
  /** Raw metric value; the panel formats it (FFA damage with the row formatter) */
  value: number;
  /** Metric label (e.g., "damage", "captures", "kills") */
  metricLabel: string;
}

/** Scoreboard state (populated when score panel is active) */
export interface ScoreboardState {
  /** Game mode determines which columns to show */
  gameMode: GameMode;
  /** Room/game name for header display */
  roomName: string;
  /** Sorted player list (by score descending) */
  players: ScoreboardPlayer[];
  /** Total player count */
  playerCount: number;
  /** MVP player info (null if no players) */
  mvp: ScoreboardMvp | null;
}

// =============================================================================
// Debug Panel Types (development only)
// =============================================================================

/** One hitch in the dev hitch log (HitchLog.ts): the gap, what the game was doing in the frame before it, and its cause. */
export interface HitchEntry {
  /** Seconds since the log started. */
  at: number;
  /** The gap between the two frames, in ms. */
  gapMs: number;
  /** The running median frame time when it happened, in ms. */
  medianMs: number;
  /** The game loop's own time in the frame before the gap, in ms. */
  loopMs: number;
  /** Whether the JS heap shrank by a megabyte or more across the gap: a garbage collection, most likely. */
  gcLikely: boolean;
  /** The packets dispatched in that frame, as "12 PLAYER_UPDATE, 1 SCORE_BOARD"; empty for none. */
  packets: string;
  /** Interface components woken in that frame, and the uiState keys that woke them. */
  uiWakes: number;
  uiKeys: string;
  /** Nameplate texts rebuilt in that frame. */
  nameplates: number;
  /** How long after its own frame time the frame after the gap began, in ms: about the gap when the main thread
   *  was busy, under a frame when the browser issued the frame late. Null where the browser keeps no frame time. */
  lateMs: number | null;
  /** The cause as HitchLog reads it from the lateness and Chrome's long-animation-frame entry; null until read. */
  cause: string | null;
}

/** The dev hitch log as its panel shows it, published once a second while it runs. */
export interface HitchLogState {
  /** Seconds the log has run. */
  seconds: number;
  frames: number;
  /** The running median frame time, in ms. */
  medianMs: number;
  /** Single skipped frames: over 24 ms and the median by half, under the hitch bar. */
  drops: number;
  /** Hitches so far, and the latest of them, newest first; the whole log is HitchLog's, for the copy. */
  hitches: number;
  entries: readonly HitchEntry[];
  /** Whether this browser reports long animation frames (Chrome 123+), the phases and scripts in the rows' cause. */
  longFrames: boolean;
}

/** The debug overlay's numbers as measured; the panel formats them. */
export interface DebugStats {
  /** Frames per second over the last refresh period. */
  fps: number;
  /** Share of wall time spent in the frame loop, as a percentage. */
  tickPercentage: number;
  /** Network ping in milliseconds. */
  ping: number;
  /** The renderer's size in pixels. */
  screenWidth: number;
  screenHeight: number;
  /** Canvas pixels per CSS pixel. */
  renderResolution: number;
  /** Players drawn and known. */
  playersActive: number;
  playersTotal: number;
  /** Mobs drawn and known. */
  mobsActive: number;
  mobsTotal: number;
  /** Live particles. */
  particles: number;
  /** Doodads on screen and placed. */
  doodadsVisible: number;
  doodadsTotal: number;
  /** Own plane's position, rounded; null when not playing. */
  coordinates: { x: number; y: number } | null;
  /** Game scale factor. */
  scale: number;
  /** Network jitter. */
  jitter: number;
  /** Average frame time over the period in ms. */
  frameTime: number;
  /** Shortest and longest frame over the period in ms; null until a frame has been timed. */
  frameTimeMin: number | null;
  frameTimeMax: number | null;
  /** JS heap usage in MB (Chrome only, null otherwise). */
  memory: number | null;
}

// =============================================================================
// Mini Leaderboard Types (in-game top-right)
// =============================================================================

/** Individual entry in the mini leaderboard */
export interface LeaderboardEntry {
  /** Player ID */
  id: number;
  /** Display rank (1-based) */
  rank: number;
  /** Country flag ISO code */
  flag: FlagCode;
  /** Player display name */
  name: string;
  /** Team id; TeamId says what it means per game type */
  team: TeamId;
  /** Player level (or 'bot' string for bots) */
  level: number | 'bot';
  /** Bounty score */
  score: number;
  /** Whether player is a bot */
  isBot: boolean;
  /** Whether player is spectating */
  isSpectating: boolean;
  /** Whether this is the current player */
  isMe: boolean;
}

/** Mini leaderboard state (in-game top-right scoreboard) */
export interface LeaderboardState {
  /** Visible leaderboard entries */
  entries: LeaderboardEntry[];
  /** Whether to show "..." before current player (when they're ranked lower) */
  showEllipsisBefore: boolean;
  /** Whether to show "..." at end (more players exist) */
  showEllipsisAfter: boolean;
}

// =============================================================================
// Context Menu Types
// =============================================================================

/** Context menu state for player interactions */
export interface ContextMenuState {
  /** Player ID to show menu for */
  playerId: number;
  /** Player name for header display */
  playerName: string;
  /** Whether player is currently ignored */
  isIgnored: boolean;
  /** Menu position: top offset in pixels */
  top: number;
  /** Menu position: left offset in pixels */
  left: number;
}

// =============================================================================
// Chat Types
// =============================================================================

/** Chat message type discriminator for styling */
export type ChatMessageType = 'public' | 'whisper-to' | 'whisper-from' | 'team' | 'system' | 'say';

/** Plain player data for chat messages (no class instances, no Pixi objects) */
export interface ChatMessagePlayer {
  /** Player ID for context menu interactions */
  id: number;
  /** Player display name */
  name: string;
  /** Country flag ISO code */
  flag: FlagCode;
  /** Pre-resolved flag tooltip label (e.g., "United States (us)") */
  flagLabel: string;
  /** Player team at send time (1 = blue, 2 = red in CTF; per-player values in
   *  FFA/BTR, so only colorize by team when the game type is a team mode) */
  team: TeamId;
}

/** Single chat message entry (plain data for UI) */
export interface ChatMessageEntry {
  /** Unique message ID for keyed rendering */
  id: number;
  /** Message type determines styling (color, tags) */
  type: ChatMessageType;
  /** Player who sent the message (null for system messages) */
  player: ChatMessagePlayer | null;
  /** Raw message text (Lit auto-escapes; URLs auto-linked at render time) */
  text: string;
  /** Whether to show warning icon (system messages only) */
  isWarning: boolean;
  /** Whether this message is from the local player (for scroll decisions) */
  isFromMe: boolean;
}

/** Chat panel state */
export interface ChatState {
  /** Chat message list (bounded by MAX_CHAT_LINES). UI.ts pushes each line
   *  as a Valtio ref, a plain record never changed once pushed; only the
   *  array is live. */
  messages: ChatMessageEntry[];
  /** Whether the chat input box is visible */
  isInputVisible: boolean;
  /** Whether the chat box is minimized */
  isMinimized: boolean;
  /** Unread message count while minimized */
  unreadCount: number;
  /** Whether the initial "Press Enter to chat" hint has been removed */
  isHintRemoved: boolean;
  /** Prefilled text for chat input (e.g., "/t ", "/w Player ") */
  inputPrefill: string | null;
  /** Mobile only: whether the bottom chat drawer is open. Also hides the touch
   *  controls while open, since the drawer overlaps the play area. */
  mobileDrawerOpen: boolean;
}

/** Debug popup state for server command replies */
export interface DebugPopupState {
  /** Formatted JSON string to display */
  json: string;
}

/** Game notification state for server errors and status messages */
export interface GameNotification {
  /** Sequence ID — component uses this to detect repeated notifications */
  id: number;
  /** Visual style: 'alert' (top 13%) or 'information' (top 18%) */
  type: 'alert' | 'information';
  /** Title badge text (e.g., "DISCONNECTED", "BANNED"). Empty string hides the badge. */
  title: string;
  /** Body text (plain text, Lit auto-escapes). Supports \n for line breaks via CSS pre-line. */
  message: string;
  /** Whether to show a RECONNECT button */
  showReconnect: boolean;
  /** Switch game button — shown when server suggests joining another game */
  switchGameButton?: { serverId: string; label: string };
  /** Auto-dismiss duration in milliseconds; 0 holds the notice until the code that raised it clears it */
  timeoutMs: number;
}

/** Player info for death/kill notification display */
export interface DeathNoticePlayer {
  /** Player display name */
  name: string;
  /** Country flag ISO code */
  flag: FlagCode;
  /** Player level (null if not shown) */
  level: number | null;
  /** Whether player is a bot */
  bot: boolean;
}

/**
 * Kill notification overlay state (top 32%)
 *
 * Shown when the player kills someone. Accumulates victims, score, and upgrades
 * over time as kill streak data arrives from the server.
 * null = hidden, non-null = visible
 */
export interface KillNoticeState {
  /** Sequence ID — component uses this to distinguish new vs updated notices */
  id: number;
  /** Victims killed in this streak */
  players: DeathNoticePlayer[];
  /** Score change to display, false = none */
  scoreChange: number | false;
  /** Upgrade count to display (0 = none) */
  upgradesChange: number;
  /** Auto-dismiss duration in milliseconds */
  timeoutMs: number;
  /** Set by the kill notice as its fade-out starts. Points or an upgrade that
   *  arrive after that open a fresh notice instead of joining this one, which
   *  would bring it back and read as a second kill. */
  fading: boolean;
}

/**
 * Death notification overlay state (top 50%)
 *
 * Shown when the player is killed by another player.
 * null = hidden, non-null = visible
 */
export interface DeathNoticeState {
  /** Sequence ID — component uses this to distinguish new vs updated notices */
  id: number;
  /** The player who killed you */
  killer: DeathNoticePlayer;
  /** Score change to display, false = none */
  scoreChange: number | false;
  /** Upgrade count to display (0 = none) */
  upgradesChange: number;
  /** Auto-dismiss duration in milliseconds */
  timeoutMs: number;
}

/** Active powerup progress bar state (null = hidden) */
export interface PowerupState {
  /** Sequence ID — component uses this to detect new vs same powerup */
  id: number;
  /** Powerup type determines color and label */
  type: 'shield' | 'inferno';
  /** Duration in ms for CSS transition countdown */
  durationMs: number;
}

/** Spectator overlay state (null = hidden) */
export interface SpectatorState {
  /** 'enter' = death screen button, 'spectating' = watching another player */
  mode: 'enter' | 'spectating';
  /** Name of player being spectated (only in 'spectating' mode) */
  playerName: string;
}

/** Map provider ID (1-5) to provider name */
export const IDENTITY_PROVIDERS: Readonly<Record<IdentityProviderId, IdentityProvider>> = {
  1: 'microsoft',
  2: 'google',
  3: 'twitter',
  4: 'reddit',
  5: 'twitch',
};

// =============================================================================
// Radio Quick Messages Types
// =============================================================================

/** Radio channel index (0 = all chat, 1 = team chat, 2 = close range) */
export type RadioChannel = 0 | 1 | 2;

/** A radio channel's name and the fixed key that opens it. */
export interface RadioChannelInfo {
  name: string;
  /** The key as the panels label it */
  key: string;
  /** The key's KeyboardEvent code, as controlKey sees it */
  code: string;
  /** Only offered in CTF, as team chat is */
  ctfOnly: boolean;
}

/** The three radio channels, indexed by RadioChannel. The keys are fixed, as in StarMash, not keybinds. */
export const RADIO_CHANNELS: readonly [RadioChannelInfo, RadioChannelInfo, RadioChannelInfo] = [
  { name: 'Radio to All', key: 'Z', code: 'KeyZ', ctfOnly: false },
  { name: 'Radio to Team', key: 'X', code: 'KeyX', ctfOnly: true },
  { name: 'Close Range', key: 'C', code: 'KeyC', ctfOnly: false },
];

/** Radio quick-message panel state (null = hidden) */
export interface RadioState {
  /** The open channel; its name and key come from RADIO_CHANNELS */
  activeChannel: RadioChannel;
  /** The channel's 10 messages */
  messages: string[];
}

/** A player as the chat's name completion sees them: /w, /ignore and /unignore complete a typed prefix from `players`. */
export interface UIPlayer {
  id: number;
  name: string;
  bot: boolean;
  /** Team id; TeamId says what it means per game type */
  team: TeamId;
}

export interface UIState {
  /** Current screen */
  screen: 'lobby' | 'game';

  /** Player name (persisted to localStorage by game) */
  playerName: string;

  /** The game regions, the games data service's list as it last changed. */
  regions: readonly GameRegion[];

  /** The region with the lowest ping, null until one has a reading. */
  closestRegionId: string | null;

  /** Currently active panel (null = no panel open) */
  activePanel: PanelType | null;

  /** Which page the menu drawer shows while activePanel is 'mainmenu'. Set
   *  as it opens, and kept while it slides shut. */
  menuPage: MenuPage;

  /** The player's own flag, the choice as made (mirrors Players' myFlag); the placeholder until known. */
  myFlag: FlagCode;

  /** Currently open top-bar popup (null = none). Mutually exclusive across top-bar panels. */
  openTopBarPanel: TopBarPanel | null;

  /** Whether the help panel shows condensed view (hides shortcuts section on first play) */
  helpCondensed: boolean;

  /** Whether the device is mobile (mirrors device.mobile for UI reactivity) */
  isMobile: boolean;

  /** Whether the device is iOS/iPadOS (mirrors device.ios). Used to tailor
   *  platform-specific copy such as the install instructions. */
  isIos: boolean;

  /** Whether the game is running as an installed home-screen app rather than
   *  in a browser tab. Suppresses the "add to home screen" prompt. */
  isInstalled: boolean;

  /** Full-screen blocking overlay message type (null = hidden) */
  bigMessage: 'mobile' | 'webgl' | null;

  /** Whether user is logged in */
  isLoggedIn: boolean;

  /** Display name from OAuth provider (null if not logged in) */
  loginName: string | null;

  /** Identity provider used for login (null if not logged in) */
  identityProvider: IdentityProvider | null;

  /** Saved region preference from settings (null = use closest) */
  savedRegion: string | null;

  /** Current game short name (e.g., "FFA #1") - set when in game */
  currentGameName: string | null;

  /** Current game ID (e.g., "ffa1") - set when in game */
  currentGameId: string | null;

  /** Current region display name (e.g., "US") - set when in game */
  currentRegionName: string | null;

  /** Current region ID (e.g., "us") - set when in game */
  currentRegionId: string | null;

  /** Invite data from URL hash (null if no invite or already processed) */
  invite: InviteData | null;

  // --- Player list (plain data for UI, synced from game) ---

  /** All players currently in the game (excludes self), rebuilt by Players on
   *  each join and leave; the chat's name completion reads it. */
  players: UIPlayer[];

  /** The ignored players' ids, UI.ts's ignore set mirrored; the chat's
   *  completion of /unignore offers these and /ignore the rest. */
  ignoredPlayerIds: number[];

  // --- In-game stats (updated during gameplay) ---

  /** Current game type; null in the lobby */
  gameType: GameMode | null;

  /** CTF only: whether the local player is currently carrying a flag (either
   *  team's). Drives the mobile DROP button's enabled state. Updated on-change
   *  from the GAME_FLAG handler, not per-frame. */
  isFlagCarrier: boolean;

  /** Current ping to server in milliseconds */
  ping: number;

  /** Player counts for the current game */
  gameStats: {
    /** Total human players */
    players: number;
    /** Bot count (excluding "Server" bot) */
    bots: number;
    /** Blue team players (CTF only) */
    blueTeam: number;
    /** Red team players (CTF only) */
    redTeam: number;
    /** Spectating players */
    spectators: number;
  };

  // --- Aircraft & Upgrade selectors ---

  /** Currently selected aircraft type */
  selectedAircraft: PlaneType;

  /** Current upgrade levels (0-5 each) */
  upgradeLevels: {
    speed: number;
    defense: number;
    energy: number;
    missile: number;
  };

  /** Available upgrade points to spend */
  availableUpgrades: number;

  // --- Score display (score-big) ---

  /** Current bounty/score */
  score: number;

  /** Current player level */
  level: number;

  /** Lifetime stats (shown in hover tooltip) */
  lifetimeStats: {
    /** Total kills across all games */
    kills: number;
    /** Total deaths across all games */
    deaths: number;
    /** Total XP earned (earnings) */
    earnings: number;
    /** XP needed for next level */
    nextLevelXp: number;
  };

  // --- Menu state ---

  /** Whether sound is enabled */
  isSoundEnabled: boolean;

  /** Whether fullscreen mode is active */
  isFullscreen: boolean;

  /** Whether the browser supports the Fullscreen API. False on iPhone
   *  Safari (no support at all); true on iPad Safari and desktop. Computed
   *  once at startup — used to hide the fullscreen toggle where it would
   *  be a no-op. */
  canFullscreen: boolean;

  /** Whether cruise mode is active (auto-throttle) */
  cruiseMode: boolean;

  // --- Keybinds panel state ---

  /** Keybinds configuration for the keybinds panel */
  keybinds: KeybindsState;

  // --- Display settings state ---

  /** Display settings (mirrors settings.display) */
  displaySettings: DisplaySettingsState;

  /** Zoom levels and which is active (mirrors the top-level zoom settings) */
  zoom: ZoomState;

  // --- Game settings state ---

  /** Game settings (mirrors settings.game) */
  gameSettings: GameSettingsState;

  // --- Invite panel state ---

  /** Generated invite link for sharing with friends */
  inviteLink: string | null;

  // --- Scoreboard panel state ---

  /** Scoreboard data (populated when score panel is active) */
  scoreboard: ScoreboardState | null;

  // --- Context menu state ---

  /** Context menu for player interactions (null = hidden) */
  contextMenu: ContextMenuState | null;

  // --- Mini leaderboard state (in-game top-right) ---

  /** Mini leaderboard data (null = hidden) */
  leaderboard: LeaderboardState | null;

  // --- Minimap positioning (for DOM element overlap avoidance) ---

  /** Minimap screen rectangle (updated on resize/scale change) */
  minimapRect: MinimapRect;

  // --- Game log (kill feed) ---

  /** Game log entries for kill feed display. GameLog pushes each as a Valtio
   *  ref, a plain record never changed once pushed; only the array is live. */
  gameLogEntries: GameLogEntry[];

  // --- Game-mode HUD (CTF flags / BTR alive count) ---

  /** CTF in-game flag status HUD (null = not in CTF or not yet initialized) */
  ctfHud: CtfHudState | null;

  /** BTR players alive count (null = not in BTR) */
  btrPlayersAlive: number | null;

  // --- CTF match result notice ---

  /** CTF match result notice state (null = hidden) */
  ctfMatchResult: CtfMatchResultState | null;

  /** BTR match result notice state (null = hidden) */
  btrMatchResult: BtrMatchResultState | null;

  /** Level up notification state (null = hidden) */
  levelUp: LevelUpState | null;

  // --- Radio quick messages ---

  /** Radio quick-message panel state (null = hidden) */
  radio: RadioState | null;

  /** Channel being edited in radio config panel (null = not editing) */
  radioConfigChannel: RadioChannel | null;

  // --- Chat state ---

  /** Chat panel state (messages, input, minimize) */
  chat: ChatState;

  // --- Debug popup (server command reply) ---

  /** Debug popup state for /debug command replies (null = hidden) */
  debugPopup: DebugPopupState | null;

  // --- Game notifications (server errors, status messages) ---

  /** Game notification state (null = hidden) */
  gameNotification: GameNotification | null;

  // --- Kill/death notification overlays ---

  /** Kill notice state — "You have destroyed..." at top 32% (null = hidden) */
  killNotice: KillNoticeState | null;

  /** Death notice state — "Destroyed by..." at top 50% (null = hidden) */
  deathNotice: DeathNoticeState | null;

  /** Active powerup progress bar state (null = hidden) */
  activePowerup: PowerupState | null;

  /** Spectator overlay state (null = hidden) */
  spectator: SpectatorState | null;

  /** Whether a cloaked enemy Prowler is currently within proximity alert range */
  prowlerAlertActive: boolean;

  /**
   * Currently tracked player ID on the minimap (single-target, null = none).
   * Double-click a name in the mini-leaderboard (or use the context menu) to
   * toggle; a ring is drawn around their minimap dot. Auto-clears when the
   * tracked player leaves the live minimap set (disconnect, spectate, game
   * switch). Doesn't reveal hidden info — just emphasizes the existing dot.
   */
  trackedPlayerId: number | null;

  /** Whether the pathfinding autopilot is currently flying the ship */
  autopilotActive: boolean;

  /**
   * Viewport dimensions and derived axis info. Updates on resize and orientation
   * change (rAF-coalesced). Maintained by `Responsive.ts`. Most layout work
   * should use CSS for this, but components occasionally need the raw values
   * for non-CSS-expressible logic (e.g. picking between two whole-layout variants).
   */
  viewport: {
    width: number;
    height: number;
    /** `min(width, height)` — the "cramped" axis. For landscape-locked games,
     *  height is usually the painful dimension; this lets components ask
     *  "is my short side cramped?" without caring about orientation. */
    shortSide: number;
    /** `max(width, height)` — the long axis. Symmetric counterpart to shortSide. */
    longSide: number;
    orientation: 'landscape' | 'portrait';
  };

  /** Formatted CTF match elapsed time (e.g. "12:34"); null outside CTF and until the match start is known */
  ctfMatchTimer: string | null;

  /** Per-plane-type rosters of non-bot, non-spec players in CTF. Null outside CTF.
   *  Keyed by `PlaneType` (1..5); `Partial` since populated lazily. */
  ctfPlaneRosters: Partial<Record<PlaneType, CtfPlaneRoster>> | null;

  /** F4 toggle — hides all Lit-rendered HUD/UI for clean screenshots / streaming */
  uiHidden: boolean;

  /** F3 toggle — hides the in-game mini-leaderboard panel only */
  leaderboardHidden: boolean;

  // --- Debug panel (development only) ---

  /** Debug stats for development panel (null when not in dev mode) */
  debugStats: DebugStats | null;

  /** Whether the dev test panel is visible */
  devTestPanel: boolean;

  /** Whether the dev hitch log is running (HitchLog.ts). The debug overlay's
   *  button and the hitch panel's mount read this rather than the log, which
   *  changes every second. */
  hitchLogOn: boolean;

  /** The dev hitch log for its panel, published once a second while it runs; null otherwise. */
  hitchLog: HitchLogState | null;
}

export const uiState = proxy<UIState>({
  screen: 'lobby',
  playerName: '',
  regions: [],
  closestRegionId: null,
  activePanel: null,
  menuPage: 'main',
  myFlag: UNKNOWN_FLAG,
  openTopBarPanel: null,
  helpCondensed: false,
  isMobile: false,
  isIos: false,
  isInstalled: false,
  bigMessage: null,
  isLoggedIn: false,
  loginName: null,
  identityProvider: null,
  savedRegion: null,
  currentGameName: null,
  currentGameId: null,
  currentRegionName: null,
  currentRegionId: null,
  invite: null,
  players: [],
  ignoredPlayerIds: [],
  gameType: null,
  isFlagCarrier: false,
  ping: 0,
  gameStats: {
    players: 0,
    bots: 0,
    blueTeam: 0,
    redTeam: 0,
    spectators: 0,
  },
  selectedAircraft: 1,
  upgradeLevels: {
    speed: 0,
    defense: 0,
    energy: 0,
    missile: 0,
  },
  availableUpgrades: 0,
  score: 0,
  level: 0,
  lifetimeStats: {
    kills: 0,
    deaths: 0,
    earnings: 0,
    nextLevelXp: 0,
  },
  isSoundEnabled: true,
  isFullscreen: false,
  canFullscreen: false,
  cruiseMode: false,
  keybinds: {
    leftColumn: [],
    rightColumn: [],
    editingBindName: null,
    editingBindIndex: 0,
    bindByName: {},
  },
  displaySettings: {
    ...DISPLAY_DEFAULTS,
    chatWidth: null,
    chatHeight: null,
    minimapScale: MINIMAP_SCALE_DEFAULT,
  },
  zoom: {
    scalingFactor: SCALE_DEFAULT,
    altScalingFactor: ALT_SCALE_DEFAULT,
    scalingAltMode: false,
    serverCap: null,
  },
  gameSettings: {
    ...GAME_DEFAULTS,
    mouseMode: false,
  },
  inviteLink: null,
  scoreboard: null,
  contextMenu: null,
  leaderboard: null,
  minimapRect: {
    top: 0,
    left: 0,
    width: 240,
    height: 120,
  },
  gameLogEntries: [],
  ctfHud: null,
  btrPlayersAlive: null,
  ctfMatchResult: null,
  btrMatchResult: null,
  levelUp: null,
  radio: null,
  radioConfigChannel: null,
  chat: {
    messages: [],
    isInputVisible: false,
    isMinimized: false,
    unreadCount: 0,
    isHintRemoved: false,
    inputPrefill: null,
    mobileDrawerOpen: false,
  },
  debugPopup: null,
  gameNotification: null,
  killNotice: null,
  deathNotice: null,
  activePowerup: null,
  spectator: null,
  prowlerAlertActive: false,
  trackedPlayerId: null,
  autopilotActive: false,
  // Overwritten synchronously by initResponsive() at boot, before any Lit
  // component renders, so the placeholder is never observed. Zeroes, obviously
  // uninitialized rather than a plausible-but-wrong size, so a boot-ordering
  // regression would surface loudly instead of silently.
  viewport: { width: 0, height: 0, shortSide: 0, longSide: 0, orientation: 'landscape' },
  ctfMatchTimer: null,
  ctfPlaneRosters: null,
  uiHidden: false,
  leaderboardHidden: false,
  debugStats: null,
  devTestPanel: false,
  hitchLogOn: false,
  hitchLog: null,
});
